// Single shared <useTodayData/> instance for the whole OLMPS shell.
//
// Mounting the hook is EXPENSIVE — one instance fires ~15 Supabase queries on
// mount (the 9 in useTodayData.load plus the 6-query FLO snapshot). Before this
// provider, OlmpsCore, NowStrip, and SyqiHome each mounted their own instance,
// tripling that fan-out to ~50 concurrent requests. Against the self-hosted
// backend and the browser's 6-connections-per-host limit, that queue drained
// over 15-27s, so the home ("Opening SyQi…") appeared to hang. Now the shell
// loads Today data ONCE and every consumer reads the same snapshot (and shares
// a single refresh()).
import React, { createContext, useContext } from 'react';
import type { UseTodayData } from './useTodayData';

const TodayDataContext = createContext<UseTodayData | null>(null);

/** Provides the shell's one Today-data instance. OlmpsCore owns the live
 *  `useTodayData(userId)` call and passes it here so it can also consume the
 *  value directly (a provider can't read its own context). */
export function TodayDataProvider({
  value,
  children,
}: {
  value: UseTodayData;
  children: React.ReactNode;
}) {
  return <TodayDataContext.Provider value={value}>{children}</TodayDataContext.Provider>;
}

/** Same as useSharedTodayData but returns null outside a provider. */
export function useOptionalSharedTodayData(): UseTodayData | null {
  return useContext(TodayDataContext);
}

/** Read the shell's shared Today-data snapshot. Must be called inside a
 *  <TodayDataProvider> (OlmpsCore mounts one around the whole shell). */
export function useSharedTodayData(): UseTodayData {
  const ctx = useContext(TodayDataContext);
  if (!ctx) {
    throw new Error('useSharedTodayData must be used inside <TodayDataProvider>');
  }
  return ctx;
}
